import {
  FC,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
  Key,
  useMemo,
  useState,
} from 'react'
import { noop } from 'lodash'
import {
  useWindowVirtualizer,
  useVirtualizer,
  type VirtualizerOptions,
  type VirtualItem,
} from '@tanstack/react-virtual'
import { cn } from '@wellesley/ui'
import { Loader2 } from 'lucide-react'
import { logger } from 'helpers/logger'
import useAsyncLock from '../useAsyncLock'
import useDebounce from '../useDebounce'
import {
  getElementOffsetTopFromDocument,
  scrollToIndex,
  waitForImages,
} from './useInfiniteScrollVirtualizer.utils'

type DefaultItem = {
  id?: string | number
}

type VirtualizerTypes = 'window' | 'container'

type VScrollEl = Element | Window

export type VirtualizerOffsetProps = {
  index: number
  itemOffset: number
  measurementsCache: VirtualItem[]
} | null

type VirtualizerProps<TItem, TItemProps extends object> = {
  // When using container type, make sure parent element has fixed height
  type?: VirtualizerTypes
  data: TItem[]
  getItemKey?: (item: TItem, index: number) => Key
  hasNextPage: boolean
  fetchNextPage: () => Promise<void> | void
  Item: FC<TItemProps>
  itemProps?: (itemData: TItem) => Partial<TItemProps>
  overscan?: number
  estimateSize?: number
  scrollPaddingStart?: number
  loadingMoreNode?: ReactNode
  endMessageNode?: ReactNode
  initialOffsetProps?: VirtualizerOffsetProps
  onOffsetChange?: (args: VirtualizerOffsetProps) => void
}

function useInfiniteScrollVirtualizer<TItem, TItemProps extends object>({
  type = 'window',
  data,
  getItemKey,
  hasNextPage,
  fetchNextPage,
  Item,
  itemProps,
  overscan = 3,
  estimateSize = 600,
  scrollPaddingStart,
  loadingMoreNode = <Loader2 className="m-auto animate-spin" size="24" />,
  endMessageNode = <p className="pt-5 text-center">No more data</p>,
  initialOffsetProps,
  onOffsetChange,
}: VirtualizerProps<TItem, TItemProps>) {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const lockedFetchNextPage = useAsyncLock(fetchNextPage)

  const shouldRestoreScroll = !!initialOffsetProps
  const isScrollRestoredRef = useRef(!shouldRestoreScroll)
  const [isReady, setIsReady] = useState(!shouldRestoreScroll)

  const _getItemKey = useCallback(
    (index: number): Key => {
      const item = data[index]

      if (getItemKey && item) {
        return getItemKey(item, index)
      }

      // fallback: try 'id', otherwise index
      return (item as unknown as DefaultItem)?.id ?? index
    },
    [data, getItemKey],
  )

  const debouncedOnOffsetChange = useDebounce(onOffsetChange || noop, 200)

  const commonProps: Pick<
    VirtualizerOptions<VScrollEl, Element>,
    | 'count'
    | 'overscan'
    | 'getItemKey'
    | 'estimateSize'
    | 'scrollPaddingStart'
    | 'initialMeasurementsCache'
    | 'onChange'
  > = useMemo(
    () => ({
      count: hasNextPage ? data.length + 1 : data.length,
      overscan,
      getItemKey: _getItemKey,
      estimateSize: () => estimateSize,
      scrollPaddingStart,
      initialMeasurementsCache: initialOffsetProps?.measurementsCache || [],
      onChange: (ins) => {
        if (!onOffsetChange) return
        if (!isScrollRestoredRef.current) return

        const topOffset =
          (ins.scrollOffset || 0) + ins.options.scrollPaddingStart

        const vItem = ins.getVirtualItemForOffset(topOffset)
        if (!vItem) return

        const offsetInItem = topOffset - vItem.start

        debouncedOnOffsetChange({
          index: vItem.index,
          itemOffset: offsetInItem,
          measurementsCache: ins.measurementsCache,
        })
      },
    }),
    [
      hasNextPage,
      data.length,
      overscan,
      _getItemKey,
      estimateSize,
      scrollPaddingStart,
      onOffsetChange,
      initialOffsetProps,
    ],
  )

  const windowVirtualizer = useWindowVirtualizer({
    ...commonProps,
    scrollMargin: getElementOffsetTopFromDocument(parentRef.current),
  })

  const containerVirtualizer = useVirtualizer({
    ...commonProps,
    getScrollElement: () => parentRef.current,
  })

  const virtualizer =
    type === 'window' ? windowVirtualizer : containerVirtualizer

  const virtualizerItems = virtualizer.getVirtualItems()

  const restoreScrollPosition = useCallback(
    async (index: number, itemOffset: number) => {
      if (!parentRef.current) return
      if (isScrollRestoredRef.current) return

      try {
        await scrollToIndex(virtualizer, index, {
          align: 'start',
        })
      } catch (e) {
        logger.error((e as Error).toString())
      }

      try {
        await waitForImages(parentRef.current)
      } catch (e) {
        logger.error((e as Error).toString())
      }

      try {
        await scrollToIndex(virtualizer, index, {
          align: 'start',
        })
      } catch (e) {
        logger.error((e as Error).toString())
      }

      virtualizer.scrollBy(itemOffset)
      isScrollRestoredRef.current = true
      setIsReady(true)
    },
    [initialOffsetProps],
  )

  const setParentRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el) return

      parentRef.current = el

      if (initialOffsetProps) {
        const { index, itemOffset } = initialOffsetProps
        void restoreScrollPosition(index, itemOffset)
      }
    },
    [initialOffsetProps],
  )

  useEffect(() => {
    if (!isScrollRestoredRef.current) return

    const lastItem = virtualizerItems.at(-1)
    if (!lastItem) return
    if (!data.length) return

    const isAtBottom = lastItem.index >= data.length - 1
    if (isAtBottom && hasNextPage) {
      void lockedFetchNextPage()
    }
  }, [hasNextPage, data.length, virtualizerItems, lockedFetchNextPage])

  return (
    <div
      ref={setParentRef}
      className={cn(
        'opacity-0',
        isReady && 'animate-[fadeIn_100ms_ease_0ms_forwards]',
        type === 'container' && 'h-full overflow-y-auto overflow-x-hidden',
      )}
    >
      <div
        className="relative w-full overscroll-none"
        style={{
          height: virtualizer.getTotalSize(),
          overflowAnchor: 'none',
        }}
      >
        <div
          className="absolute w-full will-change-transform"
          style={{
            transform: `translateY(${
              (virtualizerItems[0]?.start || 0) -
              virtualizer.options.scrollMargin
            }px)`,
          }}
        >
          {virtualizerItems.map((virtualRow) => {
            const idx = virtualRow.index
            const isLoaderRow = idx > data.length - 1
            const itemData = data[idx]

            return (
              <div
                key={virtualRow.key}
                data-index={idx}
                ref={virtualizer.measureElement}
              >
                {isLoaderRow ? (
                  hasNextPage ? (
                    loadingMoreNode
                  ) : (
                    endMessageNode
                  )
                ) : (
                  <Item
                    {...({
                      ...itemData,
                      ...(itemProps ? itemProps(itemData) : {}),
                    } as TItem & TItemProps)}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default useInfiniteScrollVirtualizer
